import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

const Draggable = memo(
    ({
        bounds = null,
        children,
        defaultPosition = { x: 0, y: 0 },
        disabled = false,
        onDrag,
        onStart,
        onStop,
        position: controlledPosition,
    }) => {
        const nodeRef = useRef(null);
        const startPositionRef = useRef({ x: 0, y: 0 });
        const startMouseRef = useRef({ x: 0, y: 0 });

        const [isDragging, setIsDragging] = useState(false);
        const [dragPosition, setDragPosition] = useState(defaultPosition);

        const currentPosition = isDragging ? dragPosition : (controlledPosition ?? dragPosition);

        useEffect(() => {
            if (!isDragging && controlledPosition) {
                setDragPosition(controlledPosition);
            }
        }, [controlledPosition, isDragging]);

        const getBoundsConstraints = useCallback(() => {
            if (!bounds || !nodeRef.current) return null;

            const node = nodeRef.current;

            if (bounds === 'parent') {
                const parent = node.parentElement;
                if (!parent) return null;

                const parentRect = parent.getBoundingClientRect();
                const nodeRect = node.getBoundingClientRect();

                return {
                    bottom: parentRect.height - nodeRect.height,
                    left: 0,
                    right: parentRect.width - nodeRect.width,
                    top: 0,
                };
            }

            if (typeof bounds === 'object') {
                return bounds;
            }

            return null;
        }, [bounds]);

        const constrainPosition = useCallback(
            (x, y) => {
                const constraints = getBoundsConstraints();
                if (!constraints) return { x, y };

                return {
                    x: Math.min(Math.max(x, constraints.left), constraints.right),
                    y: Math.min(Math.max(y, constraints.top), constraints.bottom),
                };
            },
            [getBoundsConstraints],
        );

        const handleMove = useCallback(
            (e) => {
                if (disabled) return;

                if (e.touches && e.cancelable) {
                    e.preventDefault();
                }

                const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                const clientY = e.touches ? e.touches[0].clientY : e.clientY;

                const deltaX = clientX - startMouseRef.current.x;
                const deltaY = clientY - startMouseRef.current.y;

                const newX = startPositionRef.current.x + deltaX;
                const newY = startPositionRef.current.y + deltaY;

                const constrained = constrainPosition(newX, newY);

                setDragPosition(constrained);

                if (onDrag) {
                    onDrag(e, constrained);
                }
            },
            [constrainPosition, disabled, onDrag],
        );

        const handleEnd = useCallback(
            (e) => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleEnd);
                document.removeEventListener('touchmove', handleMove);
                document.removeEventListener('touchend', handleEnd);

                document.body.style.userSelect = '';

                const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
                const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;

                const deltaX = clientX - startMouseRef.current.x;
                const deltaY = clientY - startMouseRef.current.y;

                const newX = startPositionRef.current.x + deltaX;
                const newY = startPositionRef.current.y + deltaY;

                const constrained = constrainPosition(newX, newY);

                setIsDragging(false);

                if (onStop) {
                    onStop(e, constrained);
                }
            },
            [constrainPosition, handleMove, onStop],
        );

        const handleStart = useCallback(
            (e) => {
                if (disabled) return;

                const target = e.target;
                if (
                    target.tagName === 'BUTTON' ||
                    target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.tagName === 'SELECT' ||
                    target.closest('button') ||
                    target.closest('[role="button"]')
                ) {
                    return;
                }

                const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                const clientY = e.touches ? e.touches[0].clientY : e.clientY;

                startMouseRef.current = { x: clientX, y: clientY };
                startPositionRef.current = { ...currentPosition };

                setIsDragging(true);

                document.body.style.userSelect = 'none';

                document.addEventListener('mousemove', handleMove);
                document.addEventListener('mouseup', handleEnd);
                document.addEventListener('touchmove', handleMove, { passive: false });
                document.addEventListener('touchend', handleEnd);

                if (onStart) {
                    onStart(e, currentPosition);
                }
            },
            [currentPosition, disabled, handleEnd, handleMove, onStart],
        );

        useEffect(() => {
            return () => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleEnd);
                document.removeEventListener('touchmove', handleMove);
                document.removeEventListener('touchend', handleEnd);
                document.body.style.userSelect = '';
            };
        }, [handleEnd, handleMove]);

        const child = React.Children.only(children);

        return React.cloneElement(child, {
            onMouseDown: (e) => {
                handleStart(e);
                if (child.props.onMouseDown) {
                    child.props.onMouseDown(e);
                }
            },
            onTouchStart: (e) => {
                handleStart(e);
                if (child.props.onTouchStart) {
                    child.props.onTouchStart(e);
                }
            },
            ref: nodeRef,
            style: {
                ...child.props.style,
                left: currentPosition.x,
                top: currentPosition.y,
                transform: 'none',
            },
        });
    },
);

export default Draggable;
